import { ArrowLeft } from '@phosphor-icons/react'
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import '../chat.css'

import { ErrorState } from '@/components/error-state'
import { LoadingState } from '@/components/loading-state'
import { PullToRefresh } from '@/features/discovery/components/pull-to-refresh'
import { useIsDesktop } from '@/features/discovery/hooks/use-is-desktop'
import { ApiError } from '@/lib/api'
import { useOnlineStatus } from '@/lib/ui/use-online-status'

import { chatApi, type PrivateMessage } from '../api'
import { ChatAvatar } from '../components/chat-avatar'
import { ChatComposer } from '../components/chat-composer'
import { ChatConfirmDialog } from '../components/chat-confirm-dialog'
import { ChatMenu } from '../components/chat-menu'
import { ChatNotice, type ChatNoticeState } from '../components/chat-notice'
import {
  ChatRefreshButton,
  ChatRefreshStatus,
} from '../components/chat-refresh'
import { ChatTimeline, type ChatTimelineItem } from '../components/chat-timeline'
import {
  chatErrorKey,
  latestPrivateMessageId,
} from '../format'
import { usePrivateChat } from '../hooks/use-private-chat'
import { useChatViewportLock } from '../hooks/use-chat-viewport-lock'
import { conversationDraftKey, useChatUiStore } from '../state/chat-ui-store'

const MAX_PRIVATE_MESSAGE = 2000

function toTimelineItem(message: PrivateMessage): ChatTimelineItem {
  return {
    id: message.id,
    createdAt: message.createdAt,
    kind: 'user',
    body: message.body,
    authorName: message.publicName,
    authorAvatarUrl: message.avatarUrl,
    senderId: message.senderId,
    mine: message.isMine,
    canDelete: false,
  }
}

/**
 * `/messages/:id`: private conversation with a friend. Durable read cursor,
 * pin and hide/clear; the peer profile is reachable from the header and the
 * author names. Private messages have no delete/edit endpoint upstream.
 */
export function PrivateChatScreen({ conversationId }: { conversationId: string }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isDesktop = useIsDesktop()
  useChatViewportLock()
  const online = useOnlineStatus()
  const {
    conversation,
    peer,
    messages,
    phase,
    refreshing,
    error,
    typing,
    refresh,
    setMessages,
    setPinned,
    notifyTyping,
    markRead,
  } = usePrivateChat(conversationId)

  const draftKey = conversationDraftKey(conversationId)
  const input = useChatUiStore((state) => state.drafts[draftKey] ?? '')
  const setInput = (value: string) => {
    useChatUiStore.getState().setDraft(draftKey, value)
    notifyTyping(value.trim().length > 0)
  }
  const [sending, setSending] = useState(false)
  const [notice, setNotice] = useState<ChatNoticeState | null>(null)
  const [pinBusy, setPinBusy] = useState(false)
  const [pinOverride, setPinOverride] = useState<boolean | null>(null)
  const [hideOpen, setHideOpen] = useState(false)
  const [hideBusy, setHideBusy] = useState(false)
  const [atBottom, setAtBottom] = useState(true)
  const bodyRef = useRef<HTMLDivElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const atBottomRef = useRef(true)

  const latestMessageId = latestPrivateMessageId(messages)
  const peerName = peer.name ?? t('chat.private.fallbackName')
  const pinned = pinOverride ?? peer.pinned
  const accessDenied =
    error instanceof ApiError &&
    (error.status === 403 || error.code === 'FORBIDDEN')

  useEffect(() => {
    if (phase !== 'ready' || !latestMessageId || !atBottom) return
    void markRead(latestMessageId)
  }, [phase, latestMessageId, atBottom, markRead])

  useEffect(() => {
    atBottomRef.current = atBottom
  }, [atBottom])

  useEffect(() => {
    if (messages.length === 0 || !atBottom) return
    bottomRef.current?.scrollIntoView?.({ block: 'end' })
  }, [messages.length, atBottom])

  useEffect(() => {
    const body = bodyRef.current
    const Observer = window.ResizeObserver
    if (!body || !Observer) return
    let frame = 0
    const observer = new Observer(() => {
      if (!atBottomRef.current) return
      if (frame !== 0) return
      frame = window.requestAnimationFrame(() => {
        frame = 0
        if (!atBottomRef.current) return
        // Pin directly rather than using scrollIntoView: the document has
        // smooth scrolling enabled, and scrollIntoView would animate the
        // timeline for seconds while the keyboard is already open.
        body.scrollTop = body.scrollHeight
      })
    })
    observer.observe(body)
    return () => {
      if (frame !== 0) window.cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    const body = bodyRef.current
    if (!body) return
    const scrollBody = body
    function handleScroll() {
      const scrollBottom =
        scrollBody.scrollHeight - scrollBody.scrollTop - scrollBody.clientHeight <= 96
      setAtBottom(scrollBottom)
    }
    handleScroll()
    scrollBody.addEventListener('scroll', handleScroll, { passive: true })
    return () => scrollBody.removeEventListener('scroll', handleScroll)
  }, [])

  async function send() {
    if (sending) return
    const body = input.trim()
    if (!body) return
    if (body.length > MAX_PRIVATE_MESSAGE) {
      setNotice({ messageKey: 'chat.private.tooLong', tone: 'error' })
      return
    }
    // Flush the composer on the keypress (maintainer direction): same as the
    // activity chat, so nothing typed while the request is in flight can be
    // concatenated back into the field.
    useChatUiStore.getState().clearDraft(draftKey)
    setSending(true)
    try {
      await chatApi.sendPrivateMessage(conversationId, body)
      notifyTyping(false)
      const response = await chatApi.listPrivateMessages(conversationId)
      setMessages(() => response.items)
      setAtBottom(true)
    } catch (cause) {
      const key =
        cause instanceof ApiError &&
        (cause.status === 403 || cause.code === 'FORBIDDEN')
          ? 'chat.private.sendError'
          : chatErrorKey(cause)
      // The flushed text is restored so the user can retry.
      useChatUiStore.getState().setDraft(draftKey, body)
      setNotice({ messageKey: key, tone: 'error' })
    } finally {
      setSending(false)
    }
  }

  async function togglePin() {
    if (pinBusy) return
    setPinBusy(true)
    const next = !pinned
    try {
      await chatApi.pinPrivateConversation(conversationId, next)
      setPinOverride(next)
      setPinned(next)
      setNotice({
        messageKey: next ? 'chat.notice.pinned' : 'chat.notice.unpinned',
        tone: 'success',
      })
    } catch (cause) {
      setNotice({ messageKey: chatErrorKey(cause), tone: 'error' })
    } finally {
      setPinBusy(false)
    }
  }

  async function hideConversation() {
    if (hideBusy) return
    setHideBusy(true)
    try {
      await chatApi.hidePrivateConversation(conversationId)
      useChatUiStore.getState().clearDraft(draftKey)
      setHideOpen(false)
      navigate('/chats', { replace: true })
    } catch (cause) {
      setNotice({ messageKey: chatErrorKey(cause), tone: 'error' })
    } finally {
      setHideBusy(false)
    }
  }

  async function unhideConversation() {
    if (!peer.userId) return
    try {
      await chatApi.openPrivateConversation(peer.userId)
      setNotice({ messageKey: 'chat.notice.unhidden', tone: 'success' })
      refresh()
    } catch (cause) {
      setNotice({ messageKey: chatErrorKey(cause), tone: 'error' })
    }
  }

  const menuItems = [
    {
      key: 'pin',
      labelKey: pinned ? 'chat.actions.unpin' : 'chat.actions.pin',
      onSelect: () => void togglePin(),
      disabled: pinBusy,
    },
    ...(!conversation && peer.userId
      ? [
          {
            key: 'unhide',
            labelKey: 'chat.private.unhide',
            onSelect: () => void unhideConversation(),
          },
        ]
      : []),
    {
      key: 'hide',
      labelKey: 'chat.private.hide',
      onSelect: () => setHideOpen(true),
      danger: true,
    },
  ]

  return (
    <main
      id="main-content"
      tabIndex={-1}
      aria-labelledby="private-chat-title"
      className="content-width chat-page"
    >
      <header className="chat-header">
        <Link to="/messages" className="chat-header__back">
          <ArrowLeft size={22} weight="bold" aria-hidden="true" />
          <span className="sr-only">{t('chat.private.back')}</span>
        </Link>

        {peer.userId ? (
          <Link
            to={`/users/${peer.userId}`}
            className="chat-header__peer"
            aria-label={t('chat.private.profile', { name: peerName })}
          >
            <ChatAvatar name={peerName} src={peer.avatarUrl} size="sm" />
            <h1 id="private-chat-title" className="chat-header__title">
              {peerName}
            </h1>
          </Link>
        ) : (
          <div className="chat-header__peer">
            <ChatAvatar name={peerName} src={peer.avatarUrl} size="sm" />
            <h1 id="private-chat-title" className="chat-header__title">
              {peerName}
            </h1>
          </div>
        )}

        {isDesktop ? (
          <ChatMenu
            items={menuItems}
            label={t('chat.actions.options')}
          />
        ) : null}
        {isDesktop ? (
          <ChatRefreshButton onRefresh={refresh} refreshing={refreshing} />
        ) : null}
      </header>

      <ChatNotice notice={notice} onDismiss={() => setNotice(null)} />
      {phase === 'ready' && error ? (
        <p className="chat-notice chat-notice--error" role="alert">
          {t(chatErrorKey(error, {privateChat: true}))}
        </p>
      ) : null}

      <PullToRefresh
        className="chat-page__body"
        contained
        scrollRef={bodyRef}
        onRefresh={refresh}
        refreshing={refreshing}
        disabled={isDesktop}
      >
        <ChatRefreshStatus visible={refreshing} />
        {phase === 'loading' ? (
          <LoadingState labelKey="chat.private.loading" />
        ) : phase === 'error' ? (
          <ErrorState
            error={error}
            titleKey="chat.private.errorTitle"
            bodyKey={accessDenied ? 'chat.private.unavailable' : undefined}
            onRetry={refresh}
          />
        ) : messages.length === 0 ? (
          <p className="chat-empty" role="status">
            {t('chat.private.empty')}
          </p>
        ) : (
          <ChatTimeline
            items={messages.map(toTimelineItem)}
            deletingId={null}
            onDelete={() => undefined}
          />
        )}
        {typing ? (
          <p className="chat-typing" role="status">
            {t('chat.private.typing', { name: peerName })}
          </p>
        ) : null}
        <div ref={bottomRef} />
      </PullToRefresh>

      {phase === 'ready' ? (
        <div className="chat-composer-dock" data-keyboard-sticky>
          <ChatComposer
            value={input}
            onChange={setInput}
            onSend={() => void send()}
            maxLength={MAX_PRIVATE_MESSAGE}
            hintKey="chat.private.composerHint"
            sending={sending}
            disabled={!online}
          />
        </div>
      ) : null}

      <ChatConfirmDialog
        open={hideOpen}
        titleKey="chat.confirm.privateHide.title"
        bodyKey="chat.confirm.private.body"
        confirmLabelKey="chat.private.hide"
        busy={hideBusy}
        onCancel={() => setHideOpen(false)}
        onConfirm={() => void hideConversation()}
      />
    </main>
  )
}
